import React from 'react';
import { Element, useEditor, useNode } from '@craftjs/core';
import {
  ATRIBUTO_AREA_LIBRE,
  ATRIBUTO_CAPA,
  CON_HIJOS_PROPIOS,
  ESTILO_CAPA,
  EnvoltorioLibre,
  ID_CAPA_LIBRE,
  useNodoLibre,
  PROP_ALTO_SECCION,
  PROP_ALTO_SECCION_MOVIL,
  CLASE_ALTO_SECCION_MOVIL,
  altoDeSeccion,
  estiloDeSeccion,
} from './utils/posicionLibre';
import { usePiezaActual } from './utils/piezaLibre';

/**
 * Decora cada nodo del árbol desde `<Editor onRender={...}>`, que es el punto que
 * Craft ofrece para eso. Hace dos cosas:
 *
 * 1. **A los hijos posicionados** les aplica su posición.
 * 2. **A las piezas internas de una sección** —el título, el botón…— les aplica
 *    la suya, pero SOLO si la tienen: sin `_layout` la pieza se devuelve tal
 *    cual, sin envoltorio, que es lo que mantiene intacto el DOM de todas las
 *    páginas que nunca usaron esto (ver `utils/piezaLibre`).
 * 3. **A las secciones de primer nivel que tengan elementos agregados** les
 *    dibuja la capa donde viven esos elementos (ver `ID_CAPA_LIBRE` en
 *    `utils/posicionLibre`).
 *
 * **La capa se dibuja SOLO si ya tiene algo adentro.** Es la diferencia con el
 * editor, donde se dibuja siempre para poder soltar. Así una página sin
 * elementos agregados —o guardada antes de que esto existiera— se renderiza
 * exactamente igual que antes: sin envoltorio, sin capa, mismo DOM.
 *
 * 4. **El alto manual de la sección** (`_alto`), si alguien lo movió en el
 *    editor. Vive en el envoltorio y no en la sección, así que vale para todas
 *    sin tocar ninguna. Sin `_alto` no cambia nada, y una sección que solo tiene
 *    alto manual —sin elementos agregados— igual necesita el envoltorio: por eso
 *    la condición de abajo mira las dos cosas.
 *
 * MANTENER EN SYNC con goautos-admin/src/components/builder2/RenderNodoLibre.tsx.
 */
export const RenderNodoLibre: React.FC<{ render: React.ReactElement }> = ({ render }) => {
  const { habilitado, posicion, nombre, soloEnSuSeccion } = useNodoLibre();
  const pieza = usePiezaActual();
  const { esSeccionRaiz, idCapa, altoManual, altoManualMovil } = useNode((node) => ({
    esSeccionRaiz: node.data.parent === 'ROOT' && !CON_HIJOS_PROPIOS.has(String(node.data.name)),
    idCapa: node.data.linkedNodes?.[ID_CAPA_LIBRE],
    altoManual: altoDeSeccion((node.data.props as Record<string, unknown>)?.[PROP_ALTO_SECCION]),
    altoManualMovil: altoDeSeccion((node.data.props as Record<string, unknown>)?.[PROP_ALTO_SECCION_MOVIL]),
  }));
  const { capaConContenido } = useEditor((state) => ({
    capaConContenido: !!idCapa && (state.nodes[idCapa]?.data?.nodes?.length ?? 0) > 0,
  }));

  // Pieza interna de una sección. Sin posición no se envuelve en nada: mismo DOM
  // que antes de que existieran las piezas.
  if (pieza) {
    if (!posicion) return render;
    return (
      <EnvoltorioLibre posicion={posicion} nombre={nombre} soloEnSuSeccion={soloEnSuSeccion}>
        {render}
      </EnvoltorioLibre>
    );
  }

  if (habilitado) {
    return (
      <EnvoltorioLibre posicion={posicion} nombre={nombre} soloEnSuSeccion={soloEnSuSeccion}>
        {render}
      </EnvoltorioLibre>
    );
  }

  if (!esSeccionRaiz || (!capaConContenido && !altoManual && !altoManualMovil)) return render;

  return (
    <div
      className={altoManualMovil ? CLASE_ALTO_SECCION_MOVIL : undefined}
      style={estiloDeSeccion(altoManual, altoManualMovil)}
      {...{ [ATRIBUTO_CAPA]: idCapa ?? '' }}
    >
      {render}
      {capaConContenido && (
        <Element
          id={ID_CAPA_LIBRE}
          canvas
          is="div"
          custom={{ capaLibre: true }}
          style={ESTILO_CAPA}
          {...{ [ATRIBUTO_AREA_LIBRE]: '' }}
        />
      )}
    </div>
  );
};

export default RenderNodoLibre;
